/**
 * デジタルツール認証でSSOを行うモジュール
 * Firebaseは使用せず、デジタルツールのAPIから取得したトークンを直接使用
 */

import { apiCall, setDigitalToolsAuth } from './apiHelper';
import { shouldUseSSOAuth } from './env';

const STR_TOKEN = "token";
const AUTH_TOKEN_KEY = "digital_tools_auth_token";
const USER_INFO_KEY = "digital_tools_user_info";
const APP_CONNECTION_TOKEN_KEY = "app_connection_token";
const APP_CONNECTION_TOKEN_EXPIRY_KEY = "app_connection_token_expiry";

/**
 * デジタルツール認証クラス
 * PDF仕様「デジタルツール経由でのアプリケーションログインフロー_20231201」に準拠
 */
class DigitalToolsAuth {
  constructor() {
    this.authToken = null;
    this.userInfo = null;
    this.appConnectionToken = null;
    this.authState = 'checking';
    this.callbacks = [];
    
    // ローカルストレージから認証情報を復元
    this.loadAuthFromStorage();
    this.loadAppConnectionToken();
    
    // apiHelperにインスタンスを登録（循環参照を避けるため）
    setDigitalToolsAuth(this);
  }

  /**
   * 認証状態変更のリスナーを登録
   */
  onAuthStateChanged(callback) {
    this.callbacks.push(callback);
    
    // 現在の状態を即座に通知
    callback(this.authState, this.userInfo || undefined);
    
    // 登録解除関数を返す
    return () => {
      const index = this.callbacks.indexOf(callback);
      if (index > -1) {
        this.callbacks.splice(index, 1);
      }
    };
  }

  /**
   * 認証状態を更新し、すべてのリスナーに通知
   */
  setAuthState(state, userInfo) {
    this.authState = state;
    if (userInfo) {
      this.userInfo = userInfo;
    }
    this.callbacks.forEach(callback => {
      callback(state, this.userInfo || undefined);
    });
  }

  /**
   * ローカルストレージから認証情報を読み込む
   */
  loadAuthFromStorage() {
    try {
      const tokenStr = localStorage.getItem(AUTH_TOKEN_KEY);
      const userInfoStr = localStorage.getItem(USER_INFO_KEY);
      
      if (tokenStr) {
        const token = JSON.parse(tokenStr);
        // トークンの有効期限をチェック
        if (token.expiresAt > Date.now()) {
          this.authToken = token;
          if (userInfoStr) {
            this.userInfo = JSON.parse(userInfoStr);
          }
          this.setAuthState('authenticated', this.userInfo || undefined);
          return;
        } else {
          // 有効期限切れのトークンを削除
          localStorage.removeItem(AUTH_TOKEN_KEY);
          localStorage.removeItem(USER_INFO_KEY);
        }
      }
    } catch (error) {
      console.error('認証情報の読み込みエラー:', error);
    }
    
    this.setAuthState('unauthenticated');
  }

  /**
   * 認証情報をローカルストレージに保存
   */
  saveAuthToStorage(token, userInfo) {
    try {
      localStorage.setItem(AUTH_TOKEN_KEY, JSON.stringify(token));
      if (userInfo) {
        localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
      }
    } catch (error) {
      console.error('認証情報の保存エラー:', error);
    }
  }

  /**
   * 認証情報をクリア
   */
  clearAuthFromStorage() {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(USER_INFO_KEY);
    localStorage.removeItem(APP_CONNECTION_TOKEN_KEY);
    localStorage.removeItem(APP_CONNECTION_TOKEN_EXPIRY_KEY);
    this.authToken = null;
    this.userInfo = null;
    this.appConnectionToken = null;
  }

  /**
   * アプリ接続認証トークンをローカルストレージから読み込む
   */
  loadAppConnectionToken() {
    try {
      const token = localStorage.getItem(APP_CONNECTION_TOKEN_KEY);
      const expiryStr = localStorage.getItem(APP_CONNECTION_TOKEN_EXPIRY_KEY);
      
      if (token && expiryStr) {
        const expiry = parseInt(expiryStr, 10);
        // 有効期限の5分前に期限切れとみなす
        if (Date.now() < expiry - (5 * 60 * 1000)) {
          // トークンがJSON文字列の場合（過去のバグ対応）
          let parsedToken = token;
          if (token.trim().startsWith('{')) {
            try {
              const parsed = JSON.parse(token);
              parsedToken = parsed.access_token || parsed.token || token;
            } catch (e) {
              // JSONパースに失敗した場合はそのまま使用
              parsedToken = token;
            }
          }
          this.appConnectionToken = parsedToken;
          return;
        }
        // 有効期限切れのトークンを削除
        localStorage.removeItem(APP_CONNECTION_TOKEN_KEY);
        localStorage.removeItem(APP_CONNECTION_TOKEN_EXPIRY_KEY);
      }
    } catch (error) {
      console.error('アプリ接続認証トークンの読み込みエラー:', error);
    }
  }

  /**
   * アプリ接続認証トークンを保存
   */
  saveAppConnectionToken(token, expiresIn = 3600) {
    try {
      const expiry = Date.now() + (expiresIn * 1000);
      localStorage.setItem(APP_CONNECTION_TOKEN_KEY, token);
      localStorage.setItem(APP_CONNECTION_TOKEN_EXPIRY_KEY, expiry.toString());
      this.appConnectionToken = token;
    } catch (error) {
      console.error('アプリ接続認証トークンの保存エラー:', error);
    }
  }

  /**
   * アプリ接続認証トークンを取得（client_idとclient_secretから取得）
   * OAuth2.0のclient credentials grant flowを使用
   */
  async getAppConnectionToken() {
    // 既存のトークンが有効な場合はそれを使用
    if (this.appConnectionToken) {
      const expiryStr = localStorage.getItem(APP_CONNECTION_TOKEN_EXPIRY_KEY);
      if (expiryStr) {
        const expiry = parseInt(expiryStr, 10);
        // 有効期限の5分前に期限切れとみなす
        if (Date.now() < expiry - (5 * 60 * 1000)) {
          // トークンがJSON文字列の場合（過去のバグ対応）
          if (typeof this.appConnectionToken === 'string' && this.appConnectionToken.trim().startsWith('{')) {
            try {
              const parsed = JSON.parse(this.appConnectionToken);
              const parsedToken = parsed.access_token || parsed.token;
              if (parsedToken) {
                // パースしたトークンを保存（次回から正しい形式で使用）
                this.saveAppConnectionToken(parsedToken, Math.floor((expiry - Date.now()) / 1000));
                return parsedToken;
              }
            } catch (e) {
              // JSONパースに失敗した場合はそのまま使用
            }
          }
          return this.appConnectionToken;
        }
      }
    }

    // 環境変数から認証情報を取得
    const clientId = process.env.REACT_APP_CLIENT_ID;
    const digitalToolsUrl = process.env.REACT_APP_DIGITAL_TOOLS_URL;

    if (!clientId || !digitalToolsUrl) {
      throw new Error('認証情報が設定されていません（REACT_APP_CLIENT_ID, REACT_APP_DIGITAL_TOOLS_URL）');
    }

    try {
      // PDF仕様と既存実装に準拠: /v1/oauth/client_credential/accesstoken
      // 重要: IPアドレス制限とCORSエラーを回避するため、バックエンド経由でプロキシ
      // client_secretはセキュリティのためバックエンドで管理
      const proxyUrl = '/api/auth/digital-tools/token';
      const response = await apiCall(proxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_id: clientId,
          digital_tools_url: digitalToolsUrl
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ アプリ接続認証トークン取得エラー:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`アプリ接続認証トークン取得エラー: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const responseData = await response.json();
      const tokenData = responseData.data || responseData;
      
      // バックエンドが { success: true, data: { access_token: "...", token: "...", expires_in: 3600 } } 形式で返す
      const accessToken = tokenData.access_token || tokenData.token;
      const expiresIn = tokenData.expires_in || 3600; // デフォルト1時間

      if (!accessToken || typeof accessToken !== 'string') {
        console.error('❌ レスポンスにトークンが含まれていません:', {
          responseData,
          tokenData
        });
        throw new Error('アプリ接続認証トークンがレスポンスに含まれていません');
      }
      
      // トークンの前後の空白を削除
      const trimmedToken = accessToken.trim();

      this.saveAppConnectionToken(trimmedToken, expiresIn);
      
      return trimmedToken;
    } catch (error) {
      console.error('❌ アプリ接続認証トークン取得エラー:', error instanceof Error ? error.message : String(error));
      throw error;
    }
  }

  /**
   * URLパラメータからトークンを取得
   */
  getTokenFromUrl() {
    const urlObj = new URL(window.location.href);
    const params = urlObj.searchParams;
    return params.get(STR_TOKEN);
  }

  /**
   * URLパラメータからトークンを削除
   */
  removeTokenFromUrl() {
    const urlObj = new URL(window.location.href);
    urlObj.searchParams.delete(STR_TOKEN);
    window.history.replaceState(null, '', urlObj.toString());
  }

  /**
   * 認証チェックと初期化
   * URLパラメータにトークンがある場合は認証を試みる
   */
  async checkAuth() {
    // SSO認証を使用しない環境（開発環境）では認証をスキップ
    if (!shouldUseSSOAuth()) {
      console.log('🔓 開発環境: デジタルツール認証をスキップします（認証なしでアクセス可能）');
      // 開発環境では認証済みとして扱う（デジタルツール認証なし）
      this.setAuthState('authenticated', { userId: 'dev-user', userName: '開発ユーザー' });
      return;
    }

    // SSO認証を使用する環境（検証環境・本番環境）
    console.log('🔐 SSO認証を有効化します');
    const token = this.getTokenFromUrl();
    
    if (token) {
      // URLパラメータからトークンを削除
      this.removeTokenFromUrl();
      
      // デジタルツール認証を実行
      await this.authenticateWithToken(token);
    } else {
      // 既存の認証情報をチェック
      if (this.authToken && this.authToken.expiresAt > Date.now()) {
        this.setAuthState('authenticated', this.userInfo || undefined);
      } else {
        // Cookie経由で認証を試みる
        await this.authenticateWithCookie();
      }
    }
  }

  /**
   * デジタルツールのワンタイムトークンで認証
   * PDF仕様に準拠: /v1/user-auth/usercheck エンドポイントを使用
   */
  async authenticateWithToken(token) {
    try {
      const digitalToolsUrl = process.env.REACT_APP_DIGITAL_TOOLS_URL;
      if (!digitalToolsUrl) {
        console.error('❌ REACT_APP_DIGITAL_TOOLS_URLが設定されていません');
        this.setAuthState('unauthenticated');
        return;
      }

      // アプリ接続認証トークンを取得（client_idとclient_secretから取得）
      let appConnectionToken;
      try {
        appConnectionToken = await this.getAppConnectionToken();
      } catch (error) {
        console.error('❌ アプリ接続認証トークン取得失敗:', error);
        throw new Error(`アプリ接続認証トークンの取得に失敗しました: ${error instanceof Error ? error.message : String(error)}`);
      }

      // PDF仕様に準拠したリクエストボディ
      const apiName = process.env.REACT_APP_APP_NAME || 'logistics-calc-tool';

      // 重要: IPアドレス制限とCORSエラーを回避するため、バックエンド経由でプロキシ
      const proxyUrl = '/api/auth/digital-tools/usercheck';
      const response = await apiCall(proxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          app_connection_token: appConnectionToken,
          digital_tools_url: digitalToolsUrl,
          api_name: apiName,
          auth_token: token
        }),
      });

      console.log('📥 レスポンスステータス:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ 認証APIエラー:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`認証APIエラー: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const proxyResponseData = await response.json();
      const responseData = proxyResponseData.data || proxyResponseData;
      
      // PDF仕様に準拠: dataListが文字型配列の場合はJSON.parseが必要
      let userInfo = {};
      if (responseData.dataList) {
        if (typeof responseData.dataList === 'string') {
          userInfo = JSON.parse(responseData.dataList);
        } else {
          userInfo = responseData.dataList;
        }
      }

      // レスポンスの検証
      if (responseData.success !== true || responseData.status !== 'normal') {
        console.error('❌ 認証失敗:', {
          success: responseData.success,
          status: responseData.status,
          message: responseData.message,
          statusCode: responseData.statusCode
        });
        throw new Error(`認証失敗: ${responseData.message || responseData.statusCode || 'Unknown error'}`);
      }
      
      // トークンを保存（有効期限は1時間に設定）
      const authToken = {
        token: token,
        expiresAt: Date.now() + (60 * 60 * 1000) // 1時間
      };
      
      this.authToken = authToken;
      this.userInfo = userInfo;
      
      this.saveAuthToStorage(authToken, userInfo);
      this.setAuthState('authenticated', userInfo);
    } catch (error) {
      console.error('❌ デジタルツール認証エラー:', error);
      if (error instanceof Error) {
        console.error('❌ エラーメッセージ:', error.message);
        console.error('❌ エラースタック:', error.stack);
      }
      this.setAuthState('unauthenticated');
      throw error;
    }
  }

  /**
   * Cookie経由で認証（2回目以降のログイン）
   */
  async authenticateWithCookie() {
    try {
      // 既存の認証トークンが有効な場合はそれを使用
      if (this.authToken && this.authToken.expiresAt > Date.now() && this.userInfo) {
        this.setAuthState('authenticated', this.userInfo);
        return;
      }

      // 認証トークンが無効または存在しない場合は未認証状態に設定
      this.setAuthState('unauthenticated');
    } catch (error) {
      console.error('❌ Cookie認証エラー:', error);
      this.setAuthState('unauthenticated');
    }
  }

  /**
   * デジタルツールにリダイレクト
   */
  redirectToDigitalTools() {
    // フロントエンド用のURL（ログインページ）を使用
    const frontendUrl = process.env.REACT_APP_DIGITAL_TOOLS_FRONTEND_URL || process.env.REACT_APP_DIGITAL_TOOLS_URL;
    if (frontendUrl) {
      // リダイレクト先URL（末尾のスラッシュを削除してから使用）
      const redirectUrl = frontendUrl.endsWith('/') 
        ? frontendUrl.slice(0, -1) 
        : frontendUrl;
      
      console.log('🔐 デジタルツールにリダイレクト:', {
        frontendUrl,
        redirectUrl,
        REACT_APP_DIGITAL_TOOLS_FRONTEND_URL: process.env.REACT_APP_DIGITAL_TOOLS_FRONTEND_URL,
        REACT_APP_DIGITAL_TOOLS_URL: process.env.REACT_APP_DIGITAL_TOOLS_URL
      });
      
      window.location.href = redirectUrl;
    } else {
      console.error('❌ REACT_APP_DIGITAL_TOOLS_FRONTEND_URLまたはREACT_APP_DIGITAL_TOOLS_URLが設定されていません');
      alert('認証エラー: デジタルツールのURLが設定されていません。管理者に連絡してください。');
    }
  }

  /**
   * 認証トークンを取得
   */
  async getAuthToken(forceRefresh = false) {
    // SSO認証を使用しない環境ではnullを返す
    if (!shouldUseSSOAuth()) {
      return null;
    }

    // トークンが有効期限切れの場合は再認証
    if (!this.authToken || this.authToken.expiresAt <= Date.now() || forceRefresh) {
      await this.authenticateWithCookie();
    }
    
    return this.authToken?.token || null;
  }

  /**
   * ユーザー情報を取得
   */
  getUserInfo() {
    return this.userInfo;
  }

  /**
   * 認証状態を取得
   */
  getAuthState() {
    return this.authState;
  }

  /**
   * 認証済みかどうかを判定
   */
  isAuthenticated() {
    // SSO認証を使用しない環境では常にtrueを返す（Basic認証はサーバー側で処理）
    if (!shouldUseSSOAuth()) {
      return true;
    }

    return this.authState === 'authenticated' && 
           this.authToken !== null && 
           this.authToken.expiresAt > Date.now();
  }

  /**
   * ログアウト処理
   */
  async logout() {
    this.clearAuthFromStorage();
    this.setAuthState('unauthenticated');
    this.redirectToDigitalTools();
  }
}

// シングルトンインスタンス
export const digitalToolsAuth = new DigitalToolsAuth();

